<script setup lang="ts">
import type { ArticleListItem } from '~/composables/useBlog'

const props = defineProps<{
  articles: ArticleListItem[]
}>()

// Section header entrance
const headerRef = ref<HTMLElement | null>(null)
const headerVisible = ref(false)

// Staggered card entrance — single observer on grid
const gridRef = ref<HTMLElement | null>(null)
const visibleCards = reactive(new Set<number>())

// Gentle scroll-linked horizontal parallax via scrollLeft
const sectionRef = ref<HTMLElement | null>(null)
let rafId = 0
let currentScroll = 0
let targetScroll = 0
let isLerping = false

function lerp() {
  const grid = gridRef.value
  if (!grid) return

  const diff = targetScroll - currentScroll
  if (Math.abs(diff) < 0.5) {
    currentScroll = targetScroll
    grid.scrollLeft = currentScroll
    isLerping = false
    return
  }

  currentScroll += diff * 0.08
  grid.scrollLeft = currentScroll
  requestAnimationFrame(lerp)
}

function onScroll() {
  cancelAnimationFrame(rafId)
  rafId = requestAnimationFrame(() => {
    const section = sectionRef.value
    const grid = gridRef.value
    if (!section || !grid) return

    const rect = section.getBoundingClientRect()
    const vh = window.innerHeight
    // progress: 0 when section enters bottom, 1 when it exits top
    const progress = Math.max(0, Math.min(1, (vh - rect.top) / (vh + rect.height)))
    // Scroll by ~30% of overflow — enough to hint, keeps first card anchored
    const overflow = grid.scrollWidth - grid.clientWidth
    const cardWidth = grid.firstElementChild?.getBoundingClientRect().width ?? 0
    const maxShift = Math.min(overflow * 0.3, cardWidth * 0.8)
    targetScroll = progress * maxShift

    if (!isLerping) {
      isLerping = true
      requestAnimationFrame(lerp)
    }
  })
}

onMounted(() => {
  window.addEventListener('scroll', onScroll, { passive: true })
  onScroll() // set initial position

  // Header observer
  const headerObs = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting) {
        headerVisible.value = true
        headerObs.disconnect()
      }
    },
    { threshold: 0.2 },
  )
  if (headerRef.value) headerObs.observe(headerRef.value)

  // Grid observer — one observer, staggered visibility
  const gridObs = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting) {
        props.articles.forEach((_, i) => {
          setTimeout(() => visibleCards.add(i), i * 80)
        })
        gridObs.disconnect()
      }
    },
    { threshold: 0.15 },
  )
  if (gridRef.value) gridObs.observe(gridRef.value)

  onUnmounted(() => {
    headerObs.disconnect()
    gridObs.disconnect()
    window.removeEventListener('scroll', onScroll)
    cancelAnimationFrame(rafId)
  })
})
</script>

<template>
  <section ref="sectionRef" class="max-w-7xl mx-auto px-4 py-8">
    <!-- Section header with border draw animation -->
    <header
      ref="headerRef"
      :class="[
        'stories-header flex flex-col md:flex-row md:items-end justify-between pb-4 mb-8 relative',
        headerVisible && 'stories-header--visible',
      ]"
    >
      <div class="flex flex-col gap-1.5">
        <p
          :class="[
            'font-label font-light text-xs leading-4 tracking-wider text-brand transition-all duration-500',
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2.5',
          ]"
        >
          FROM THE MAGAZINE
        </p>
        <h2
          :class="[
            'font-serif font-normal text-2xl md:text-4xl text-dark transition-all duration-500 delay-100',
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2.5',
          ]"
        >
          Recent Stories
        </h2>
      </div>
      <NuxtLink
        to="/journal"
        :class="[
          'mt-4 md:mt-0 transition-opacity duration-500 delay-200',
          headerVisible ? 'opacity-100' : 'opacity-0',
        ]"
      >
        <AppButton type="Ghost" size="Small" label="View all stories" :showIconRight="true" />
      </NuxtLink>
    </header>

    <!-- Card grid -->
    <div
      ref="gridRef"
      class="flex gap-4 overflow-x-auto pb-4 scrollbar-hide"
    >
      <div
        v-for="(article, i) in articles"
        :key="article.handle"
        class="w-[75%] sm:w-[45%] lg:w-[calc(25%-12px)] flex-shrink-0"
      >
        <AppCard
          type="Feature"
          :categories="article.category"
          :title="article.title"
          :short-description="article.excerpt"
          :image="article.image"
          :slug="`/journal/${article.handle}`"
          :animated="true"
          :elevated="true"
          :tinted="true"
          :visible="visibleCards.has(i)"
        />
      </div>
    </div>
  </section>
</template>

<style scoped>
/* Bottom border draws left → right */
.stories-header::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 1px;
  background: var(--color-border-default);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 400ms ease-out 200ms;
}

.stories-header--visible::after {
  transform: scaleX(1);
}

/* Hide scrollbar */
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
</style>
